import useLocalStorage from "@/utils/useLocalStorage";
import { useUnmount } from "ahooks";
import { Tooltip } from "antd";
import classNames from "classnames";
import React, { forwardRef, useCallback, useImperativeHandle } from "react";
import { When } from "react-if";
import HBIcon from "../HBIcon";
import styles from "./index.module.less";

/**
 *
 * @param {{
 *  localStorageKey: string,
 *  visible?: boolean,
 *  children?: React.ReactNode,
 *  hideType?: "click" | "hover",
 *  containerClassName?: string,
 *  closeClassName?: string,
 *  contentWidth?: number,
 *  className?: string,
 *  dotStyle?: React.CSSProperties,
 *  showDot?: boolean,
 *  needUserId: boolean,
 *  customprops?: any,
 *  once?: boolean,
 *  containerStyle?: React.CSSProperties,
 * } & import("antd").TooltipProps} props
 * @param {*} ref
 * @returns
 */
function RawUserGuideTooltip(
  {
    title,
    localStorageKey,
    visible = true, // 方便其他条件进行控制显示隐藏
    children,
    hideType = "click",
    containerClassName,
    closeClassName,
    contentWidth,
    className,
    dotStyle,
    showDot,
    needUserId = true,
    customprops,
    once = false,
    containerStyle = {},
    ...props
  },
  ref
) {
  const [tooltipVisible, setTooltipVisible] = useLocalStorage(localStorageKey, {
    loadingValue: false,
    defaultValue: true,
    initValue: false,
    needUserId,
  });
  useUnmount(() => {
    if (once && tooltipVisible && visible) {
      setTooltipVisible(false);
    }
  });

  const onClick = useCallback(() => {
    if (hideType === "click") {
      setTooltipVisible(false);
    }
  }, [hideType, setTooltipVisible]);

  const onMouseEnter = useCallback(() => {
    if (hideType === "hover") {
      setTooltipVisible(false);
    }
  }, [hideType, setTooltipVisible]);

  useImperativeHandle(ref, () => {
    return {
      hide() {
        setTooltipVisible(false);
      },
    };
  });

  return (
    <Tooltip
      overlayClassName={classNames([styles.tooltip, className])}
      getPopupContainer={(triggerNode) => triggerNode}
      title={
        title && (
          <div
            className={styles.content}
            style={contentWidth ? { width: contentWidth } : null}
          >
            <span>{title}</span>
            <HBIcon
              className={classNames([closeClassName, styles.close])}
              type="ic_clear2"
              onClick={(e) => {
                e.stopPropagation();
                setTooltipVisible(false);
              }}
            />
          </div>
        )
      }
      visible={tooltipVisible && visible}
      {...props}
    >
      <div
        onClickCapture={onClick}
        onMouseEnter={onMouseEnter}
        className={classNames(containerClassName, styles.container)}
        data-tooltip-show={tooltipVisible && visible}
        style={containerStyle}
      >
        {!!children &&
          React.cloneElement(children, {
            customprops: customprops,
          })}

        <When condition={showDot && tooltipVisible && visible}>
          <div className={styles.dot} style={dotStyle}></div>
        </When>
      </div>
    </Tooltip>
  );
}

const UserGuideTooltip = forwardRef(RawUserGuideTooltip);

export default UserGuideTooltip;
